import { detectGameType } from '../gameTypeUtils';

export const CLOSE_MAX_MARGIN = 2;
export const COMPETITIVE_MAX_MARGIN_15 = 4;
export const COMPETITIVE_MAX_MARGIN_21 = 5;

export const closenessThresholdsFor = (pointsTarget = 21) => {
  const target = pointsTarget === 15 ? 15 : 21;
  return {
    pointsTarget: target,
    closeMax: CLOSE_MAX_MARGIN,
    competitiveMax: target === 15 ? COMPETITIVE_MAX_MARGIN_15 : COMPETITIVE_MAX_MARGIN_21,
  };
};
/** At least one of the other three on court is new, on average. */
export const GOOD_GAME_MIN_NOVELTY = 1 / 3;
export const SOLID_GAME_MIN_NOVELTY = 0.5;
export const STUCK_PAIR_MIN_SHARE = 0.5;
export const STUCK_PAIR_MIN_GAMES = 2;
export const OFF_MEDIAN_GAMES = 1;
export const PAPER_BALANCE_TIGHT = 50;
export const PAPER_BALANCE_GOOD = 100;

const UNBALANCED_GAME_TYPE_IDS = new Set([6, 8, 9]);

const pairKey = (a, b) => [a, b].sort().join('|');

const normalizeGender = (gender) => {
  const value = String(gender || '').toLowerCase();
  if (value === 'male' || value === 'female') return value;
  return 'unspecified';
};

const playerIdFromRoster = (row) => row?.player_id || row?.players?.id || row?.id || null;

const playerNameFromRoster = (row) => {
  if (row?.name) return row.name;
  const first = row?.first_name || row?.players?.first_name || '';
  const last = row?.last_name || row?.players?.last_name || '';
  return `${first} ${last}`.trim() || 'Unknown';
};

const playerGenderFromRoster = (row, genderById) =>
  normalizeGender(row?.gender || row?.players?.gender || genderById[playerIdFromRoster(row)]);

const teamPlayers = (game, team) => {
  if (team === 1) return game?.team_1_players || game?.team1Players || [];
  return game?.team_2_players || game?.team2Players || [];
};

const courtPlayers = (game) => [...teamPlayers(game, 1), ...teamPlayers(game, 2)];

const playerId = (player) => player?.id || player?.player_id || null;

const hasScore = (game) =>
  game?.team_1_score != null && game?.team_2_score != null;

const scoreMargin = (game) =>
  Math.abs(Number(game.team_1_score) - Number(game.team_2_score));

const classifyCloseness = (margin, { closeMax, competitiveMax }) => {
  if (margin <= closeMax) return 'close';
  if (margin <= competitiveMax) return 'competitive';
  return 'oneSided';
};

export const sortGamesChronologically = (games = []) =>
  [...games].sort((a, b) => {
    const timeA = a.start_time || a.game_created_at || '';
    const timeB = b.start_time || b.game_created_at || '';
    if (timeA !== timeB) return timeA < timeB ? -1 : 1;
    return (a.game_number || 0) - (b.game_number || 0);
  });

const average = (values) => {
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const median = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) return (sorted[mid - 1] + sorted[mid]) / 2;
  return sorted[mid];
};

const share = (part, whole) => (whole > 0 ? part / whole : null);

const winningTeamNumber = (game) => {
  if (game.winning_team === 1 || game.winning_team === 'Team 1') return 1;
  if (game.winning_team === 2 || game.winning_team === 'Team 2') return 2;
  return null;
};

const teamAvgElo = (game, team) => {
  if (team === 1) return game.team_1_avg_starting_elo ?? game.team1AvgElo ?? null;
  return game.team_2_avg_starting_elo ?? game.team2AvgElo ?? null;
};

const splitHalves = (items) => {
  if (items.length <= 1) return { first: items, second: [] };
  const mid = Math.ceil(items.length / 2);
  return { first: items.slice(0, mid), second: items.slice(mid) };
};

export const buildSessionHealthVerdict = ({
  scoredGames = 0,
  competitiveShare = null,
  noveltyAvg = null,
  gamesSpread = null,
} = {}) => {
  if (scoredGames === 0) {
    return 'No scored games yet — analysis will appear once scores are logged.';
  }

  const competitiveOk = competitiveShare != null && competitiveShare >= 0.4;
  const noveltyOk = noveltyAvg != null && noveltyAvg >= 0.45;
  const fairnessOk = gamesSpread == null || gamesSpread <= 2;

  if (competitiveOk && noveltyOk && fairnessOk) {
    return 'Competitive games, and players kept rotating.';
  }
  if (competitiveOk && !noveltyOk) {
    return 'Scores were competitive, but the same groups kept meeting.';
  }
  if (!competitiveOk && noveltyOk && fairnessOk) {
    return 'Players rotated well, but many games were one-sided.';
  }
  if (!fairnessOk) {
    return 'Playing time was uneven — some players sat more than others.';
  }
  return 'A mixed night — check the numbers below.';
};

/**
 * Reconstruct session health from finished games and the wrap-up roster.
 * Novelty is scored at the moment each game started, not from the final interaction map.
 */
const partnershipPairKey = (partnership) => {
  const idA = partnership?.player1Id || partnership?.player1_id || partnership?.player_1_id;
  const idB = partnership?.player2Id || partnership?.player2_id || partnership?.player_2_id;
  if (!idA || !idB) return null;
  return pairKey(idA, idB);
};

export const buildSessionHealth = (games = [], sessionPlayers = [], partnerships = [], options = {}) => {
  const thresholds = closenessThresholdsFor(options.pointsTarget);
  const orderedGames = sortGamesChronologically(games);
  const seenPairs = new Set();
  const seenQuartets = new Set();
  const partnerCounts = new Map();
  const gamesPlayedById = new Map();
  const genderById = {};
  const nameById = {};

  const closeness = { close: 0, competitive: 0, oneSided: 0, unscored: 0 };
  const noveltyValues = [];
  let goodGames = 0;
  let solidGames = 0;
  let quartetRepeats = 0;
  let paperWithin50 = 0;
  let paperWithin100 = 0;
  let ratedGames = 0;
  let upsets = 0;
  let alteredGames = 0;
  const gameTypes = { mens: 0, womens: 0, mixed: 0, other: 0, unbalanced: 0 };

  orderedGames.forEach((game) => {
    const players = courtPlayers(game).filter((player) => playerId(player));
    const ids = players.map(playerId);

    players.forEach((player) => {
      const id = playerId(player);
      gamesPlayedById.set(id, (gamesPlayedById.get(id) || 0) + 1);
      if (!genderById[id] && player.gender) genderById[id] = normalizeGender(player.gender);
      if (!nameById[id] && player.name) nameById[id] = player.name;
    });

    const playerNovelties = ids.map((id) => {
      const others = ids.filter((otherId) => otherId !== id);
      if (others.length === 0) return 1;
      const novelCount = others.filter((otherId) => !seenPairs.has(pairKey(id, otherId))).length;
      return novelCount / others.length;
    });
    const gameNovelty = average(playerNovelties) ?? 1;
    noveltyValues.push(gameNovelty);

    if (ids.length === 4) {
      const quartet = [...ids].sort().join('|');
      if (seenQuartets.has(quartet)) quartetRepeats += 1;
      else seenQuartets.add(quartet);
    }

    const team1 = teamPlayers(game, 1).map(playerId).filter(Boolean);
    const team2 = teamPlayers(game, 2).map(playerId).filter(Boolean);
    [team1, team2].forEach((team) => {
      for (let i = 0; i < team.length; i += 1) {
        for (let j = i + 1; j < team.length; j += 1) {
          const key = pairKey(team[i], team[j]);
          partnerCounts.set(key, (partnerCounts.get(key) || 0) + 1);
        }
      }
    });

    for (let i = 0; i < ids.length; i += 1) {
      for (let j = i + 1; j < ids.length; j += 1) {
        seenPairs.add(pairKey(ids[i], ids[j]));
      }
    }

    if (game.is_altered) alteredGames += 1;

    const detected = detectGameType(teamPlayers(game, 1), teamPlayers(game, 2));
    const typeId = detected?.id;
    if (typeId === 2) gameTypes.mens += 1;
    else if (typeId === 3) gameTypes.womens += 1;
    else if (typeId === 1) gameTypes.mixed += 1;
    else if (UNBALANCED_GAME_TYPE_IDS.has(typeId)) gameTypes.unbalanced += 1;
    else gameTypes.other += 1;

    const elo1 = teamAvgElo(game, 1);
    const elo2 = teamAvgElo(game, 2);
    if (elo1 != null && elo2 != null) {
      ratedGames += 1;
      const eloDiff = Math.abs(elo1 - elo2);
      if (eloDiff <= PAPER_BALANCE_TIGHT) paperWithin50 += 1;
      if (eloDiff <= PAPER_BALANCE_GOOD) paperWithin100 += 1;

      const winner = winningTeamNumber(game);
      if (winner === 1 && elo1 < elo2) upsets += 1;
      if (winner === 2 && elo2 < elo1) upsets += 1;
    }

    if (!hasScore(game)) {
      closeness.unscored += 1;
      return;
    }

    const margin = scoreMargin(game);
    const band = classifyCloseness(margin, thresholds);
    closeness[band] += 1;

    if (margin <= thresholds.competitiveMax && gameNovelty >= GOOD_GAME_MIN_NOVELTY) {
      goodGames += 1;
    }
    if (margin <= thresholds.competitiveMax && gameNovelty >= SOLID_GAME_MIN_NOVELTY) {
      solidGames += 1;
    }
  });

  const scoredGames = closeness.close + closeness.competitive + closeness.oneSided;
  const halves = splitHalves(noveltyValues);

  sessionPlayers.forEach((row) => {
    const id = playerIdFromRoster(row);
    if (!id) return;
    if (!nameById[id]) nameById[id] = playerNameFromRoster(row);
    if (!genderById[id]) genderById[id] = playerGenderFromRoster(row, genderById);
    const rosterGames = row.games_played;
    if (rosterGames != null && !gamesPlayedById.has(id)) {
      gamesPlayedById.set(id, Number(rosterGames) || 0);
    }
  });

  const playersWhoPlayed = [...gamesPlayedById.entries()]
    .filter(([, count]) => count > 0)
    .map(([id, count]) => ({
      id,
      name: nameById[id] || 'Unknown',
      gender: genderById[id] || 'unspecified',
      games: count,
    }));

  const attendance = playersWhoPlayed.reduce(
    (acc, player) => {
      acc[player.gender] += 1;
      acc.total += 1;
      return acc;
    },
    { male: 0, female: 0, unspecified: 0, total: 0 }
  );

  const noShows = sessionPlayers.filter((row) => row.exit_reason === 'no_show').length;

  const gameCounts = playersWhoPlayed.map((player) => player.games);
  const medianGames = median(gameCounts);
  const offMedian = playersWhoPlayed
    .filter((player) => medianGames != null && Math.abs(player.games - medianGames) > OFF_MEDIAN_GAMES)
    .sort((a, b) => b.games - a.games);

  const avgForGender = (gender) => {
    const counts = playersWhoPlayed
      .filter((player) => player.gender === gender)
      .map((player) => player.games);
    return average(counts);
  };

  const playingTime = {
    avg: average(gameCounts),
    min: gameCounts.length ? Math.min(...gameCounts) : null,
    max: gameCounts.length ? Math.max(...gameCounts) : null,
    median: medianGames,
    offMedian,
    avgByGender: {
      male: avgForGender('male'),
      female: avgForGender('female'),
    },
  };

  const playerCount = playersWhoPlayed.length;
  const possiblePairs = playerCount >= 2 ? (playerCount * (playerCount - 1)) / 2 : 0;
  const formalPartnerships = new Set(
    (partnerships || []).map(partnershipPairKey).filter(Boolean)
  );
  const stuckPairs = [...partnerCounts.entries()]
    .map(([key, count]) => {
      const [idA, idB] = key.split('|');
      const gamesA = gamesPlayedById.get(idA) || 0;
      const gamesB = gamesPlayedById.get(idB) || 0;
      const shareA = gamesA > 0 ? count / gamesA : 0;
      const shareB = gamesB > 0 ? count / gamesB : 0;
      const qualifies =
        (gamesA >= STUCK_PAIR_MIN_GAMES && shareA > STUCK_PAIR_MIN_SHARE) ||
        (gamesB >= STUCK_PAIR_MIN_GAMES && shareB > STUCK_PAIR_MIN_SHARE);
      return {
        playerA: { id: idA, name: nameById[idA] || 'Unknown' },
        playerB: { id: idB, name: nameById[idB] || 'Unknown' },
        partnerships: count,
        share: Math.max(shareA, shareB),
        qualifies,
        key,
      };
    })
    .filter((pair) => pair.qualifies && !formalPartnerships.has(pair.key))
    .sort((a, b) => b.share - a.share || b.partnerships - a.partnerships)
    .map(({ key, qualifies, ...pair }) => pair);

  const closeShare = share(closeness.close, scoredGames);
  const competitiveShare = share(closeness.close + closeness.competitive, scoredGames);
  const noveltyAvg = average(noveltyValues);
  const gamesSpread =
    playingTime.min != null && playingTime.max != null
      ? playingTime.max - playingTime.min
      : null;

  return {
    totalGames: orderedGames.length,
    scoredGames,
    closeness,
    closenessThresholds: thresholds,
    closeShare,
    competitiveShare,
    goodGames,
    solidGames,
    novelty: {
      overall: noveltyAvg,
      firstHalf: average(halves.first),
      secondHalf: average(halves.second),
    },
    quartetRepeats,
    paperBalance: {
      within50: paperWithin50,
      within100: paperWithin100,
      ratedGames,
      hasEloData: ratedGames > 0,
    },
    upsets,
    alteredGames,
    attendance: {
      ...attendance,
      noShows,
    },
    playingTime,
    gameTypes,
    social: {
      pairsSharedCourt: seenPairs.size,
      possiblePairs,
      coverage: share(seenPairs.size, possiblePairs),
      stuckPairs,
    },
    verdict: buildSessionHealthVerdict({
      scoredGames,
      competitiveShare,
      noveltyAvg,
      gamesSpread,
    }),
  };
};

export default buildSessionHealth;
